<template>
    <Form
        ref="formRef"
        class="flex flex-wrap items-end gap-1"
        :initialValues
        :resolver
        validateOnMount
        @submit="(e) => submit(e as FormSubmitEvent<AxFormCnCompletionSchema>)"
    >
        <template v-for="(value, index) in fields" :key="value.id">
            <AxFieldInputCn
                :field="value"
                :rules="rules"
                :disabled
                @change="handleChange"
            />
            <span v-if="separator && Number(index) < (fields.length - 1)" class="mb-1">
                {{ separator }}
            </span>
        </template>
    </Form>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Form, type FormInstance, type FormSubmitEvent } from '@primevue/forms';
import type { GetCnDataField, GetCnDataRule } from '@/shared/DTO/GED';
import * as z from 'zod';
import AxFieldInputCn from './AxFieldInputCn.vue';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { CN_RULE_FIELD_INPUT_TYPE } from '@/shared/constants/cnRuleField';

const props = defineProps<{
    fields: GetCnDataField[];
    separator: string;
    disabled: boolean;
    rules: GetCnDataRule;
    initialValues?: Record<string, string>;
}>();

const { t } = useI18n();

const formRef = ref<FormInstance | null>(null);

function buildRegexPattern(regex: string): RegExp {
    let regexPattern = regex;
    if (!regexPattern.startsWith('^')) {
        regexPattern = `^${regexPattern}`;
    }
    if (!regexPattern.endsWith('$')) {
        regexPattern = `${regexPattern}$`;
    }
    return new RegExp(regexPattern);
}

function createFieldValidator(field: GetCnDataField, separator?: string): z.ZodString | null {
    if (field.inputType === CN_RULE_FIELD_INPUT_TYPE.NUM_AUTO) {
        return z.string();
    }

    if (field.inputType === CN_RULE_FIELD_INPUT_TYPE.TEXT) {
        let validator = z.string().min(1, t('common.required_field'));
        if (field.regex) {
            try {
                const regex = buildRegexPattern(field.regex);
                validator = validator.regex(regex, t('workspace.create_pending_document.field.name_separator_regex_error'));
            } catch (error) {
                console.warn(`Invalid regex pattern for field ${field.id}:`, field.regex, error);
            }
        }
        if (separator) {
            validator = validator.refine((val) => !val.includes(separator), {
                message: t('workspace.create_pending_document.field.name_separator_error'),
            });
        }
        return validator;
    }

    if (field.inputType === CN_RULE_FIELD_INPUT_TYPE.SELECT) {
        let validator = z.string().min(1, t('common.required_field'));
        if (field.regex) {
            try {
                const regex = buildRegexPattern(field.regex);
                validator = validator.regex(regex, t('workspace.create_pending_document.field.name_separator_regex_error'));
            } catch (error) {
                console.warn(`Invalid regex pattern for field ${field.id}:`, field.regex, error);
            }
        }
        if (separator) {
            validator = validator.refine((val) => !val.includes(separator), {
                message: t('workspace.create_pending_document.field.name_separator_error'),
            });
        }
        if (field.options && field.options.length > 0) {
            validator = validator.refine((val) => field.options!.includes(val), {
                message: t('workspace.create_pending_document.field.name_options_error'),
            });
        }
        return validator;
    }

    return null;
}

const initialValues = computed(() => ({
    fields: Object.fromEntries(
        props.fields.map((field) => [field.id, props.initialValues?.[field.id] ?? ''])
    ),
}));

const schema = computed(() => {
    const fieldsSchema: Record<string, z.ZodString> = {};

    props.fields.forEach((field) => {
        const validator = createFieldValidator(field, props.separator);
        if (validator) {
            fieldsSchema[field.id] = validator;
        }
    });

    return z.object({
        fields: Object.keys(fieldsSchema).length > 0
            ? z.object(fieldsSchema)
            : z.record(z.string(), z.string()),
    });
});

export type AxFormCnCompletionSchema = z.infer<typeof schema.value>;

const resolver = zodResolver(schema.value);

function handleChange() {
    const fields = Object.fromEntries(
        Object.entries(formRef.value?.states?.fields ?? {}).map(([key, field]) => [key, field.value])
    );
    emit('change', fields);
}

function submit(e: FormSubmitEvent<AxFormCnCompletionSchema>) {
    emit('submit', e);
}

const emit = defineEmits<{
    submit: [e: FormSubmitEvent<AxFormCnCompletionSchema>];
    change: [fields: Record<string, string>];
}>();

defineExpose({
    formRef,
    validate: () => formRef.value?.validate(),
});
</script>
